import { Badge, Box, Button, Collapsible, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { ChevronDown, ChevronRight, Plus, Radio, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ConnectionStatus, MqttQoS, Subscription } from '@/lib/mqtt-types'

interface SubscriptionsPanelProps {
  subscriptions: Subscription[]
  status: ConnectionStatus
  onSubscribe: (topic: string, qos: MqttQoS) => void
  onUnsubscribe: (topic: string) => void
  onRemove: (topic: string) => void
  defaultOpen?: boolean
}

export function SubscriptionsPanel({
  subscriptions,
  status,
  onSubscribe,
  onUnsubscribe,
  onRemove,
  defaultOpen = true,
}: SubscriptionsPanelProps) {
  const [newTopic, setNewTopic] = useState('')
  const [newQos, setNewQos] = useState<MqttQoS>(0)
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const isConnected = status === 'connected'

  // Update open state when defaultOpen changes
  useEffect(() => {
    setIsOpen(defaultOpen)
  }, [defaultOpen])

  const handleAddSubscription = () => {
    if (!newTopic.trim()) return
    onSubscribe(newTopic.trim(), newQos)
    setNewTopic('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddSubscription()
    }
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" bg="bg.panel">
      {/* Collapsible Header */}
      <Button
        variant="ghost"
        w="100%"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
        p={4}
        h="auto"
        borderRadius={isOpen ? 'lg lg 0 0' : 'lg'}
      >
        <HStack gap={2}>
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Text fontWeight="bold">Subscriptions</Text>
          {subscriptions.length > 0 && (
            <Badge size="sm" colorPalette="brand" variant="subtle">
              {subscriptions.length}
            </Badge>
          )}
        </HStack>
      </Button>

      <Collapsible.Root open={isOpen}>
        <Collapsible.Content>
          <Box px={4} pb={4}>
            {/* Add subscription form - can be used before connecting */}
            <HStack gap={2} mb={4} mt={4}>
              <Input
                size="sm"
                placeholder="Topic (e.g., sensors/#)"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyPress={handleKeyPress}
                flex={1}
              />
              <HStack gap={1}>
                {([0, 1, 2] as MqttQoS[]).map((qos) => (
                  <Button
                    key={qos}
                    size="sm"
                    variant={newQos === qos ? 'solid' : 'outline'}
                    colorPalette={newQos === qos ? 'brand' : undefined}
                    onClick={() => setNewQos(qos)}
                    minW={8}
                  >
                    {qos}
                  </Button>
                ))}
              </HStack>
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleAddSubscription}
                disabled={!newTopic.trim()}
              >
                <Plus size={16} />
              </Button>
            </HStack>

            {/* Subscription list */}
            <VStack gap={2} align="stretch" maxH="300px" overflowY="auto">
              {subscriptions.length === 0 ? (
                <Box py={4} textAlign="center" color="content.secondary">
                  <Radio
                    size={24}
                    style={{ margin: '0 auto 8px', display: 'block', opacity: 0.5 }}
                  />
                  <Text fontSize="sm">No subscriptions yet</Text>
                  <Text fontSize="xs">
                    {isConnected
                      ? 'Add a topic above to start receiving messages'
                      : 'Add topics above - they will be subscribed when you connect'}
                  </Text>
                </Box>
              ) : (
                subscriptions.map((sub) => (
                  <HStack
                    key={sub.topic}
                    p={2}
                    borderRadius="md"
                    bg="bg.subtle"
                    justify="space-between"
                  >
                    <HStack gap={2} flex={1} minW={0}>
                      <Box
                        w={3}
                        h={3}
                        borderRadius="full"
                        bg={sub.color}
                        opacity={sub.active ? 1 : 0.3}
                        flexShrink={0}
                      />
                      <VStack align="start" gap={0} minW={0} flex={1}>
                        <Text fontSize="sm" fontWeight="medium" truncate maxW="100%">
                          {sub.topic}
                        </Text>
                        <HStack gap={2}>
                          <Badge size="sm" variant="subtle">
                            QoS {sub.qos}
                          </Badge>
                          {sub.messageCount > 0 && (
                            <Badge size="sm" colorPalette="brand" variant="subtle">
                              {sub.messageCount} msg{sub.messageCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {!sub.active && (
                            <Badge size="sm" colorPalette="yellow" variant="subtle">
                              Pending
                            </Badge>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>

                    <HStack gap={1}>
                      {sub.active ? (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="yellow"
                          onClick={() => onUnsubscribe(sub.topic)}
                          disabled={!isConnected}
                          title="Unsubscribe"
                        >
                          Unsub
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="green"
                          onClick={() => onSubscribe(sub.topic, sub.qos)}
                          disabled={!isConnected}
                          title="Resubscribe"
                        >
                          Sub
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => onRemove(sub.topic)}
                        title="Remove"
                      >
                        <X size={14} />
                      </Button>
                    </HStack>
                  </HStack>
                ))
              )}
            </VStack>

            {/* Quick topics - available even before connecting */}
            {subscriptions.length === 0 && (
              <Box mt={4} pt={4} borderTopWidth="1px">
                <Text fontSize="xs" color="content.secondary" mb={2}>
                  Quick subscribe:
                </Text>
                <HStack gap={1} flexWrap="wrap">
                  {[
                    '#',
                    'Enterprise/#',
                    'ConnCorp/#',
                    'WindCorp/#',
                    'OGCorp/#',
                    'HPSCorp/#',
                    'spBv1.0/#',
                  ].map((topic) => (
                    <Button
                      key={topic}
                      size="xs"
                      variant="outline"
                      onClick={() => onSubscribe(topic, 0)}
                    >
                      {topic}
                    </Button>
                  ))}
                </HStack>
              </Box>
            )}
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  )
}

export default SubscriptionsPanel
